/**
 * @file index.tsx
 *
 * @fileoverview Displays an animated Text with extra words that appear
 * and disappear in a typewriter effect.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Text, TextProps } from "./../03-text";

export interface TextAnimatedProps extends TextProps {
  /**
   * Words or sentences to rotate next to the main text.
   */
  rotatingSentences?: string[] | React.ReactNode[];
  /**
   * Speed of typing (milliseconds per character)
   *
   * @default 100
   */
  typingSpeed?: number;
  /**
   * Delay between sentences (milliseconds). How much time each sentence
   * stays visible before it is replaced by the next one.
   *
   * @default 2000
   */
  sentenceDelay?: number;
}

export const TextAnimated: React.FC<TextAnimatedProps> = ({
  className = "",
  rotatingSentences = [],
  typingSpeed = 100,
  sentenceDelay = 2000,
  ...props
}) => {
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const currentSentence = useMemo(() => {
    return rotatingSentences[currentSentenceIndex] as string;
  }, [rotatingSentences, currentSentenceIndex]);

  useEffect(() => {
    if (rotatingSentences.length === 0) return;

    const handleTyping = () => {
      if (isDeleting) {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentSentenceIndex(
            (prevIndex) => (prevIndex + 1) % rotatingSentences.length
          );
        }
      } else {
        if (displayText.length < currentSentence.length) {
          setDisplayText(currentSentence.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), sentenceDelay);
        }
      }
    };

    const typingTimer = setTimeout(
      handleTyping,
      isDeleting ? typingSpeed / 2 : typingSpeed
    );

    return () => clearTimeout(typingTimer);
  }, [
    displayText,
    isDeleting,
    currentSentence,
    rotatingSentences,
    typingSpeed,
    sentenceDelay,
  ]);

  return (
    <Text {...props} className={`${className} text-animated`}>
      {props.children}
      <span>{displayText}</span>
    </Text>
  );
};

export default TextAnimated;
